import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { CategoryModal } from './CategoryModal';
import { ConfirmDialog } from './ConfirmDialog';
import { categoriesService } from '../api/services';
import type { Category, CreateCategory } from '../types';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';

export function Categories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoriesService.getCategories();
        setCategories(data.categories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleAddCategory = () => {
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setIsModalOpen(true);
  };

  const handleDeleteCategory = (id: string) => {
    setCategoryToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    const deletePromise = categoriesService.deleteCategory(categoryToDelete);
    toast.promise(deletePromise, {
      loading: 'Deleting category...',
      success: () => {
        setCategories(categories.filter(c => c.id !== categoryToDelete));
        return 'Category deleted successfully';
      },
      error: 'Failed to delete category',
    });
  };

  const handleSaveCategory = async (category: Category | CreateCategory) => {
    const isEditing = 'id' in category && !!category.id;
    const savePromise = (async () => {
      if (isEditing) {
        const updated = await categoriesService.updateCategory((category as Category).id, category as Category);
        setCategories(categories.map(c => c.id === (category as Category).id ? updated : c));
        return updated;
      } else {
        const created = await categoriesService.createCategory(category as CreateCategory);
        setCategories([...categories, created]);
        return created;
      }
    })();

    toast.promise(savePromise, {
      loading: isEditing ? 'Updating category...' : 'Creating category...',
      success: () => {
        setIsModalOpen(false);
        return `Category ${isEditing ? 'updated' : 'created'} successfully`;
      },
      error: (err) => `Failed to save category: ${err.message || 'Unknown error'}`,
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <div className="h-32 bg-gray-100" />
              <CardContent className="p-6">
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-full mb-4" />
                <Skeleton className="h-4 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Categories</h2>
          <p className="text-muted-foreground">Organize your products into categories</p>
        </div>
        <Button onClick={handleAddCategory}>
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <Card key={category.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-none bg-card/40 backdrop-blur-md ring-1 ring-border/50 rounded-2xl">
            <div className="relative h-64 w-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
              {category.thumbnailUrl ? (
                <img
                  src={category.thumbnailUrl}
                  alt={category.name}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 opacity-40 group-hover:opacity-60 transition-opacity">
                  <span className="text-7xl">�</span>
                </div>
              )}

              <div className="absolute bottom-3 right-3">
                <Badge
                  variant={category.isActive ? 'default' : 'secondary'}
                  className={`${category.isActive ? 'bg-green-500 text-white border-none shadow-lg shadow-green-500/30' : 'bg-slate-700 text-slate-200 border-none shadow-lg shadow-slate-900/30'} px-2.5 py-1 text-[10px] uppercase tracking-widest font-black rounded-full`}
                >
                  {category.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>

            <CardContent className="p-4 bg-background/80 backdrop-blur-xl">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-lg font-extrabold tracking-tight text-foreground truncate flex-grow">
                  {category.name}
                </h3>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-10 h-10 rounded-xl hover:bg-primary/10 hover:text-primary transition-all duration-300"
                    onClick={() => handleEditCategory(category)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-10 h-10 rounded-xl hover:bg-destructive/10 hover:text-destructive transition-all duration-300"
                    onClick={() => handleDeleteCategory(category.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {isModalOpen && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveCategory}
        />
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={confirmDelete}
        title="Delete Category"
        description="Are you sure you want to delete this category? This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
      />
    </div>
  );
}
